import React, { useCallback, useState } from 'react';
import { PenLine, Plus, Download, CheckCircle2, ShieldCheck } from 'lucide-react';
import SectionShell from '../plan/SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

const ROLES = [
  'Crew Leader', 'Crew Member', 'Steward', 'Safety Observer',
  'System Operator', 'Supervisor', 'Other',
];

/**
 * SignOffSection — Phase E.
 *
 * Every crew member sign-off appends an append-only `signoff_added` event.
 * The `Complete close-out` button becomes enabled after at least one
 * sign-off is captured. Once complete, the PDF Export button is highlighted.
 */
export default function SignOffSection({ draftId, closeoutState, ownerEmail, onChanged, isReadOnly }) {
  const signoffs = closeoutState?.signoffs || [];
  const completed_at = closeoutState?.completed_at;
  const [name, setName] = useState('');
  const [role, setRole] = useState('Crew Leader');
  const [statement, setStatement] = useState('Work complete. No injuries. Equipment restored to service.');
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);

  const submit = useCallback(async () => {
    if (!name.trim() || !role || !draftId) return;
    setBusy(true);
    try {
      await jobBriefApi.addSignoff(draftId, {
        name: name.trim(), role, statement: statement.trim(),
        added_by: ownerEmail,
      });
      setName(''); setStatement('Work complete. No injuries. Equipment restored to service.');
      onChanged?.();
    } finally { setBusy(false); }
  }, [name, role, statement, draftId, ownerEmail, onChanged]);

  const complete = useCallback(async () => {
    if (!draftId || signoffs.length === 0) return;
    setCompleting(true);
    try {
      await jobBriefApi.completeCloseout(draftId);
      onChanged?.();
    } finally { setCompleting(false); }
  }, [draftId, signoffs.length, onChanged]);

  const pdfUrl = draftId ? jobBriefApi.getCloseoutPdfUrl(draftId) : null;

  return (
    <SectionShell
      icon={PenLine}
      title="Sign-Offs"
      subtitle="Every crew member must sign off before the close-out is complete."
      color="#a78bfa"
      badge={completed_at ? 'Complete' : signoffs.length ? `${signoffs.length} signed` : 'Required'}
      testId="jbv2-section-signoffs"
    >
      {signoffs.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-signoffs-empty">
          No sign-offs yet. At least one is required before close-out can be completed.
        </div>
      )}
      {signoffs.map((s) => (
        <div key={s.id} className="jbv2-row" data-testid={`jbv2-signoff-row-${s.id}`}>
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat">
              <ShieldCheck size={12} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
              {s.name}
            </div>
            <div className="jbv2-hp-row-meta">
              {s.role} · {s.created_at && new Date(s.created_at).toLocaleString()}
            </div>
            {s.statement && <div className="jbv2-row-note">&ldquo;{s.statement}&rdquo;</div>}
          </div>
        </div>
      ))}

      {!isReadOnly && !completed_at && (
        <div className="jbv2-add-row" style={{ flexDirection: 'column', alignItems: 'stretch' }} data-testid="jbv2-signoff-form">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              data-testid="jbv2-signoff-name"
              aria-label="Signer name"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="jbv2-signoff-role"
              aria-label="Role"
              style={{ maxWidth: 200 }}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <textarea
            className="jbv2-field-textarea"
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="Attestation statement"
            rows={2}
            data-testid="jbv2-signoff-statement"
            aria-label="Statement"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || !name.trim() || !role}
            data-testid="jbv2-signoff-add-btn"
          >
            <Plus size={13} /> Sign
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
        {!completed_at && !isReadOnly && (
          <button
            type="button"
            className="jbv2-row-btn"
            onClick={complete}
            disabled={completing || signoffs.length === 0}
            data-testid="jbv2-closeout-complete-btn"
            style={{
              background: 'rgba(134,239,172,0.10)',
              borderColor: 'rgba(134,239,172,0.55)',
              color: '#86efac',
            }}
          >
            <CheckCircle2 size={13} /> {completing ? 'Completing…' : 'Complete close-out'}
          </button>
        )}
        {completed_at && (
          <div
            className="jbv2-hp-row-cite"
            data-testid="jbv2-closeout-completed-badge"
            style={{ padding: '6px 10px', fontSize: 12 }}
          >
            <CheckCircle2 size={12} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
            Close-out completed {new Date(completed_at).toLocaleString()}
          </div>
        )}
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="jbv2-row-btn"
            data-testid="jbv2-closeout-pdf-btn"
            style={{
              background: 'rgba(167,139,250,0.10)',
              borderColor: 'rgba(167,139,250,0.55)',
              color: '#c4b5fd',
              textDecoration: 'none',
            }}
          >
            <Download size={13} /> Download PDF
          </a>
        )}
      </div>
    </SectionShell>
  );
}
